import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDndContext, ClientRect, UniqueIdentifier } from "utils/core/src";
import { useIsomorphicLayoutEffect, useUniqueId } from "@dnd-kit/utilities";

import type { Disabled, SortingStrategy } from "../types";
import { getSortedRects, itemsEqual, normalizeDisabled } from "../utilities";
import { rectSortingStrategy } from "../strategies";

export interface Props {
  children: React.ReactNode;
  items: (UniqueIdentifier | { id: UniqueIdentifier })[];
  strategy?: SortingStrategy;
  id?: string;
  disabled?: boolean | Disabled;
  disableSort: boolean;
  setDisableSort(disable: boolean): void;
}

const ID_PREFIX = "Sortable";

interface ContextDescriptor {
  activeIndex: number;
  containerId: string;
  disabled: Disabled;
  disableTransforms: boolean;
  items: UniqueIdentifier[];
  itemsHaveChanged: boolean;
  rows: any;
  overIndex: number;
  useDragOverlay: boolean;
  sortedRects: ClientRect[];
  strategy: SortingStrategy;
  direction: string;
  setDirection(direction: string): void;
  overPercentage: number;
  setOverPercentage(percentage: number): void;
  nextRow: any;
  setNextRow(row: any): void;
  disableSort: boolean;
  setDisableSort(disable: boolean): void;
}

export const Context = React.createContext<ContextDescriptor>({
  activeIndex: -1,
  containerId: ID_PREFIX,
  disableTransforms: false,
  itemsHaveChanged: false,
  items: [],
  rows: [],
  overIndex: -1,
  useDragOverlay: false,
  sortedRects: [],
  strategy: rectSortingStrategy,
  disabled: {
    draggable: false,
    droppable: false,
  },
  direction: "up",
  setDirection: () => {},
  overPercentage: 0,
  setOverPercentage: () => {},
  nextRow: null,
  setNextRow: () => {},
  disableSort: false,
  setDisableSort: () => {},
});

export function SortableContext({
  children,
  id,
  items: userDefinedItems,
  disableSort,
  setDisableSort,
  strategy = rectSortingStrategy,
  disabled: disabledProp = false,
}: Props) {
  const {
    active,
    dragOverlay,
    droppableRects,
    over,
    measureDroppableContainers,
    measuringScheduled,
  } = useDndContext();

  const [direction, setDirection] = useState("up");
  const [nextRow, setNextRow] = useState();
  const [overPercentage, setOverPercentage] = useState(0);
  const containerId = useUniqueId(ID_PREFIX, id);
  const useDragOverlay = Boolean(dragOverlay.rect !== null);
  const items = useMemo<UniqueIdentifier[]>(
    () =>
      userDefinedItems.map((item) =>
        typeof item === "object" && "id" in item ? item.id : item
      ),
    [userDefinedItems]
  );

  const isDragging = active != null;
  const activeIndex = active ? items.indexOf(active.id) : -1;
  const overIndex = over ? items.indexOf(over.id) : -1;
  const previousItemsRef = useRef(items);
  const itemsHaveChanged = !itemsEqual(items, previousItemsRef.current);

  const disableTransforms =
    (overIndex !== -1 && activeIndex === -1) || itemsHaveChanged;
  const disabled = normalizeDisabled(disabledProp);

  useIsomorphicLayoutEffect(() => {
    if (itemsHaveChanged && isDragging && !measuringScheduled) {
      measureDroppableContainers(items);
    }
  }, [
    itemsHaveChanged,
    items,
    isDragging,
    measureDroppableContainers,
    measuringScheduled,
  ]);

  useEffect(() => {
    previousItemsRef.current = items;
  }, [items]);

  const contextValue = useMemo(
    (): ContextDescriptor => ({
      activeIndex,
      containerId,
      disabled,
      disableTransforms,
      items,
      itemsHaveChanged,
      rows: userDefinedItems,
      overIndex,
      useDragOverlay,
      sortedRects: getSortedRects(items, droppableRects),
      strategy,
      direction,
      setDirection,
      overPercentage,
      setOverPercentage,
      nextRow,
      setNextRow,
      disableSort,
      setDisableSort,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      activeIndex,
      containerId,
      disabled.draggable,
      disabled.droppable,
      disableTransforms,
      items,
      direction,
      nextRow,
      overPercentage,
      overIndex,
      droppableRects,
      useDragOverlay,
      strategy,
    ]
  );

  return <Context.Provider value={contextValue}>{children}</Context.Provider>;
}
